/**
 * DividendsPage — Dividend tracking & income forecasting dashboard.
 * Route: /app/dividends
 */
import React, { useEffect, useState, useCallback } from 'react'
import {
  Banknote, CalendarDays, TrendingUp, BarChart2,
  Award, Star, Search, ChevronRight, ArrowUpRight, ShieldCheck,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts'
import { useDividends } from '@/hooks/useDividends'
import { clsx } from 'clsx'
import type { DividendHolding, DividendConfidence, DividendAristocrat, TopYieldStock } from '@/lib/types'
import IncomeTabs from '@/components/ui/IncomeTabs'

// ── Helpers ───────────────────────────────────────────────────────────────────

// US-universe panels only (aristocrats / top-yield / per-share history) — native $ figures.
function fmtMoney(n: number | null | undefined, decimals = 2): string {
  if (n == null) return 'N/A'
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`
}

// Portfolio income figures are computed from GBP holding values on the backend — show £.
function fmtGBP(n: number | null | undefined, decimals = 2): string {
  if (n == null) return 'N/A'
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`
}

function fmtPct(n: number | null | undefined): string {
  if (n == null) return 'N/A'
  return `${n.toFixed(2)}%`
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function daysUntil(iso: string): number {
  const d = new Date(iso + 'T00:00:00')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((d.getTime() - today.getTime()) / 86400000)
}

const FREQ_COLORS: Record<string, string> = {
  Monthly:      'text-emerald bg-emerald/10',
  Quarterly:    'text-blue-light bg-blue/10',
  'Semi-Annual':'text-amber-400 bg-amber-400/10',
  Annual:       'text-purple-400 bg-purple-400/10',
  Unknown:      'text-text-muted bg-surface-card',
}

// ── Confidence Badge ─────────────────────────────────────────────────────────

const CONF_STYLES: Record<string, string> = {
  high:   'text-emerald-600 bg-emerald-500/10 border border-emerald-500/20',
  medium: 'text-amber-500  bg-amber-400/10  border border-amber-400/20',
  low:    'text-rose-500   bg-rose-500/10   border border-rose-500/20',
}

const CONF_LABEL: Record<string, string> = {
  high:   'Reliable',
  medium: 'Uncertain',
  low:    'Low',
}

function ConfidenceBadge({
  confidence,
  showTooltip = false,
}: {
  confidence: DividendConfidence | null | undefined
  showTooltip?: boolean
}) {
  const [open, setOpen] = useState(false)
  if (!confidence) return null
  const { level, reasons } = confidence
  const style = CONF_STYLES[level] ?? CONF_STYLES.low
  const label = CONF_LABEL[level] ?? level

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={e => { e.stopPropagation(); if (showTooltip) setOpen(o => !o) }}
        className={clsx(
          'px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none cursor-default',
          style,
          showTooltip && 'cursor-help',
        )}
        title={reasons.join(' | ')}
      >
        {label}
      </button>
      {showTooltip && open && (
        <div
          className="absolute z-50 left-0 top-full mt-1 w-64 rounded-lg border border-border
                     bg-surface-card shadow-lg p-3 text-xs text-text-primary space-y-1"
          role="tooltip"
        >
          <p className="font-semibold text-text-primary mb-1">Confidence signals</p>
          {reasons.map((r, i) => (
            <p key={i} className="text-text-muted leading-snug">• {r}</p>
          ))}
          <button
            type="button"
            onClick={e => { e.stopPropagation(); setOpen(false) }}
            className="mt-2 text-text-muted hover:text-text-primary text-[10px]"
          >
            Close
          </button>
        </div>
      )}
    </span>
  )
}

// ── Reliable vs Speculative Income Banner ─────────────────────────────────────

function ConfidenceSplitBanner() {
  const { portfolio, portfolioLoading } = useDividends()

  const reliable    = portfolio?.reliable_annual_income_gbp
  const speculative = portfolio?.speculative_annual_income_gbp
  const breakdown   = portfolio?.confidence_breakdown

  // Only render when confidence data is present
  if (portfolioLoading || reliable == null || speculative == null) return null

  const total = (reliable ?? 0) + (speculative ?? 0)
  const reliablePct = total > 0 ? Math.round((reliable / total) * 100) : 0

  return (
    <div className="col-span-3 card p-4">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={16} className="text-emerald" />
        <span className="panel-title">Income Reliability Split</span>
        <span className="text-xs text-text-muted ml-1">
          based on yield validity, payment history, and frequency confidence
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div className="rounded-lg p-3 bg-emerald-500/5 border border-emerald-500/20">
          <p className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wide mb-0.5">
            Reliable Income
          </p>
          <p className="text-xl font-bold font-mono text-emerald">
            £{(reliable ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}/yr
          </p>
          <p className="text-xs text-text-muted mt-0.5">
            {breakdown?.high_count ?? 0} holdings · high confidence only
          </p>
        </div>
        <div className="rounded-lg p-3 bg-amber-400/5 border border-amber-400/20">
          <p className="text-[10px] text-amber-500 font-semibold uppercase tracking-wide mb-0.5">
            Speculative Income
          </p>
          <p className="text-xl font-bold font-mono text-amber-400">
            £{(speculative ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}/yr
          </p>
          <p className="text-xs text-text-muted mt-0.5">
            {(breakdown?.medium_count ?? 0) + (breakdown?.low_count ?? 0)} holdings · uncertain or unverified
          </p>
        </div>
      </div>

      {/* Progress bar — width is a dynamic runtime value, CSS custom property is
          the idiomatic way to pass it in Tailwind; avoids raw inline style. */}
      <div className="w-full h-2 rounded-full bg-amber-400/20 overflow-hidden">
        <div
          className="h-full rounded-full bg-emerald transition-all duration-500"
          // eslint-disable-next-line react/forbid-component-props
          style={{ '--conf-w': `${reliablePct}%`, width: 'var(--conf-w)' } as React.CSSProperties}
        />
      </div>
      <p className="text-[10px] text-text-muted mt-1">
        {reliablePct}% of forecast income classified as reliable
      </p>
    </div>
  )
}

// ── Income Summary Card ───────────────────────────────────────────────────────

function IncomeSummary() {
  const { portfolio, portfolioLoading } = useDividends()

  if (portfolioLoading) {
    return (
      <div className="col-span-3 card p-5 animate-pulse">
        <div className="h-4 bg-surface-card rounded w-1/3 mb-4" />
        <div className="grid grid-cols-3 gap-4">
          {[0, 1, 2].map(i => <div key={i} className="h-12 bg-surface-card rounded" />)}
        </div>
      </div>
    )
  }

  const annual = portfolio?.total_annual_income ?? 0
  const monthly = annual / 12
  const yoc = portfolio?.portfolio_yield ?? 0

  const metrics = [
    { label: 'Monthly Average',        value: fmtGBP(monthly, 0), icon: TrendingUp, color: 'text-blue-light' },
    { label: 'Portfolio Yield',         value: fmtPct(yoc),          icon: BarChart2, color: 'text-amber-400' },
  ]

  return (
    <div className="col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-4 stagger-children">
      {/* The page's one number — dividend income per year */}
      <div className="card p-5">
        <p className="panel-title flex items-center gap-2">
          <Banknote size={14} className="text-emerald" aria-hidden />
          Dividend income per year
        </p>
        <p className="stat-hero text-text-primary mt-1">{fmtGBP(annual, 0)}</p>
      </div>
      {metrics.map(({ label, value, icon: Icon, color }) => (
        <div key={label} className="card p-5">
          <div className="flex items-center gap-2 mb-2">
            <Icon size={16} className={color} />
            <span className="text-xs text-text-muted">{label}</span>
          </div>
          <p className={clsx('stat-value font-mono', color)}>{value}</p>
        </div>
      ))}
    </div>
  )
}

// ── Portfolio Dividends Table ─────────────────────────────────────────────────

function PortfolioTable({ onSelectTicker }: { onSelectTicker: (t: string) => void }) {
  const { portfolio, portfolioLoading } = useDividends()

  return (
    <div className="card p-4">
      <h2 className="panel-title mb-3 flex items-center gap-2">
        <Banknote size={16} className="text-emerald" />
        Portfolio Dividend Income
      </h2>

      {portfolioLoading ? (
        <div className="animate-pulse space-y-2">
          {[0, 1, 2, 3].map(i => <div key={i} className="h-10 bg-surface-card rounded" />)}
        </div>
      ) : !portfolio?.holdings.length ? (
        <p className="text-text-muted text-sm">No dividend-paying holdings found.</p>
      ) : (
        <div className="overflow-x-auto">
          <div className="table-responsive">
          <table className="table-pro w-full text-sm">
            <thead>
              <tr>
                <th className="py-2 pr-3">Ticker</th>
                <th className="py-2 pr-3 hidden md:table-cell">Name</th>
                <th className="!text-right py-2 pr-3">Yield</th>
                <th className="!text-right py-2 pr-3 hidden sm:table-cell">Div/Share</th>
                <th className="!text-right py-2 pr-3">Annual Inc</th>
                <th className="!text-center py-2 pr-3 hidden sm:table-cell">Confidence</th>
                <th className="!text-center py-2 pr-3">Frequency</th>
                <th className="py-2">Next Ex-Date</th>
                <th className="!text-right py-2" />
              </tr>
            </thead>
            <tbody>
              {portfolio.holdings.map((h: DividendHolding) => (
                <tr
                  key={h.ticker}
                  className="cursor-pointer"
                  onClick={() => onSelectTicker(h.ticker)}
                >
                  <td className="py-2 pr-3 font-mono font-semibold text-blue-light">{h.ticker}</td>
                  <td className="py-2 pr-3 text-text-primary hidden md:table-cell truncate max-w-[140px]">{h.name}</td>
                  <td className="py-2 pr-3 text-right font-mono text-emerald">
                    {h.div_yield != null ? fmtPct(h.div_yield) : 'N/A'}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono text-text-primary hidden sm:table-cell">
                    {h.annual_div_per_share != null ? fmtMoney(h.annual_div_per_share, 4) : 'N/A'}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono font-semibold text-text-primary">
                    {h.annual_income != null ? fmtGBP(h.annual_income, 0) : 'N/A'}
                  </td>
                  <td className="py-2 pr-3 text-center hidden sm:table-cell">
                    <ConfidenceBadge confidence={h.confidence} showTooltip />
                  </td>
                  <td className="py-2 pr-3 text-center">
                    {h.frequency && h.frequency !== 'Unknown' ? (
                      <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', FREQ_COLORS[h.frequency] ?? 'text-text-muted')}>
                        {h.frequency}
                      </span>
                    ) : (
                      <span className="text-text-muted text-xs">—</span>
                    )}
                  </td>
                  <td className="py-2 text-text-muted text-xs">
                    {h.next_ex_date ? formatDate(h.next_ex_date) : '—'}
                  </td>
                  <td className="py-2 text-right">
                    <ChevronRight size={14} className="text-text-muted inline" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Monthly Income Forecast Bar Chart ─────────────────────────────────────────

const MONTH_ORDER = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function MonthlyForecast() {
  const { portfolio, portfolioLoading } = useDividends()

  const chartData = MONTH_ORDER.map(m => ({
    month: m,
    income: portfolio?.monthly_forecast?.[m] ?? 0,
  }))

  const maxIncome = Math.max(...chartData.map(d => d.income), 1)

  return (
    <div className="card p-4">
      <h2 className="panel-title mb-3 flex items-center gap-2">
        <TrendingUp size={16} className="text-blue-light" />
        Monthly Income Forecast
      </h2>

      {portfolioLoading ? (
        <div className="h-48 bg-surface-card rounded animate-pulse" />
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis hide />
            <Tooltip
              formatter={(v: number) => [fmtGBP(v, 0), 'Expected Income']}
              contentStyle={{
                background: 'var(--color-surface-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                fontSize: '12px', color: 'rgb(var(--text-primary))'}}
            />
            <Bar dataKey="income" radius={[3, 3, 0, 0]}>
              {chartData.map((entry) => (
                <Cell
                  key={entry.month}
                  fill={entry.income >= maxIncome * 0.7 ? '#10b981' : entry.income > 0 ? '#3b82f6' : '#374151'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

// ── Upcoming Ex-Dates Calendar ────────────────────────────────────────────────

function ExDateCalendar() {
  const { calendar, calendarLoading } = useDividends()
  const events = calendar?.events ?? []

  return (
    <div className="card p-4">
      <h2 className="panel-title mb-3 flex items-center gap-2">
        <CalendarDays size={16} className="text-amber-400" />
        Upcoming Ex-Dividend Dates
        <span className="ml-auto text-xs text-text-muted font-normal normal-case tracking-normal">Next 90 days</span>
      </h2>

      {calendarLoading ? (
        <div className="animate-pulse space-y-2">
          {[0, 1, 2, 3, 4].map(i => <div key={i} className="h-10 bg-surface-card rounded" />)}
        </div>
      ) : events.length === 0 ? (
        <p className="text-text-muted text-sm">No upcoming ex-dividend dates found.</p>
      ) : (
        <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
          {events.map((e, i) => {
            const days = daysUntil(e.date)
            return (
              <div
                key={`${e.ticker}-${e.date}-${i}`}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm',
                  e.in_portfolio ? 'bg-blue/5 border border-blue/20' : 'bg-surface-card',
                )}
              >
                <span className="font-mono font-semibold text-blue-light w-14 shrink-0">{e.ticker}</span>
                <span className="text-text-primary flex-1 truncate hidden sm:block">{e.name}</span>
                <span className="text-text-muted text-xs w-24 shrink-0">{formatDate(e.date)}</span>
                <span className={clsx(
                  'text-xs font-medium w-12 text-right shrink-0',
                  days <= 7 ? 'text-rose-light' : days <= 14 ? 'text-amber-400' : 'text-text-muted',
                )}>
                  {days}d
                </span>
                <span className="font-mono text-xs text-emerald w-16 text-right shrink-0">
                  {fmtMoney(e.amount_per_share, 4)}
                </span>
                {e.in_portfolio && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue/15 text-blue-light shrink-0">OWN</span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Dividend Aristocrats ───────────────────────────────────────────────────────

function AristocratsPanel({ onSelectTicker }: { onSelectTicker: (t: string) => void }) {
  const { aristocrats, aristocratsLoading } = useDividends()
  const [search, setSearch] = useState('')

  const filtered = (aristocrats?.aristocrats ?? []).filter(a =>
    !search || (a.ticker ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (a.name ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (a.sector ?? '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="card p-4">
      <h2 className="panel-title mb-3 flex items-center gap-2">
        <Award size={16} className="text-amber-400" />
        Dividend Aristocrats
        <span className="ml-1 text-xs text-text-muted font-normal normal-case tracking-normal">25+ yrs of growth</span>
      </h2>

      <div className="relative mb-3">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search aristocrats..."
          className="input pl-8 pr-3 py-1.5 text-sm"
        />
      </div>

      {aristocratsLoading ? (
        <div className="animate-pulse space-y-2">
          {[0, 1, 2, 3].map(i => <div key={i} className="h-10 bg-surface-card rounded" />)}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="table-responsive">
          <table className="table-pro w-full text-sm">
            <thead>
              <tr>
                <th className="py-2 pr-3">Ticker</th>
                <th className="py-2 pr-3 hidden md:table-cell">Name</th>
                <th className="py-2 pr-3 hidden lg:table-cell">Sector</th>
                <th className="!text-right py-2 pr-3">Yrs</th>
                <th className="!text-right py-2 pr-3">Yield</th>
                <th className="!text-right py-2">Div Rate</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a: DividendAristocrat) => (
                <tr
                  key={a.ticker}
                  onClick={() => onSelectTicker(a.ticker)}
                  className="cursor-pointer"
                >
                  <td className="py-2 pr-3 font-mono font-semibold text-blue-light">{a.ticker}</td>
                  <td className="py-2 pr-3 text-text-primary truncate max-w-[140px] hidden md:table-cell">{a.name}</td>
                  <td className="py-2 pr-3 text-text-muted text-xs hidden lg:table-cell">{a.sector}</td>
                  <td className="py-2 pr-3 text-right">
                    <span className="text-amber-400 font-semibold">{a.years_of_growth}</span>
                  </td>
                  <td className="py-2 pr-3 text-right font-mono text-emerald">
                    {a.current_yield > 0 ? fmtPct(a.current_yield) : 'N/A'}
                  </td>
                  <td className="py-2 text-right font-mono text-text-primary">
                    {a.annual_div_rate > 0 ? fmtMoney(a.annual_div_rate, 2) : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Top Yield Panel ───────────────────────────────────────────────────────────

function TopYieldPanel({ onSelectTicker }: { onSelectTicker: (t: string) => void }) {
  const { topYield, topYieldLoading } = useDividends()

  return (
    <div className="card p-4">
      <h2 className="panel-title mb-3 flex items-center gap-2">
        <Star size={16} className="text-blue-light" />
        Top Dividend Yields
      </h2>

      {topYieldLoading ? (
        <div className="animate-pulse space-y-2">
          {[0, 1, 2, 3, 4].map(i => <div key={i} className="h-10 bg-surface-card rounded" />)}
        </div>
      ) : (
        <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
          {(topYield?.stocks ?? []).map((s: TopYieldStock, i) => (
            <div
              key={s.ticker}
              onClick={() => onSelectTicker(s.ticker)}
              className="hover-lift flex items-center gap-3 px-3 py-2 rounded-lg bg-surface-card hover:bg-surface-hover
                         transition-colors cursor-pointer text-sm"
            >
              <span className="text-text-muted text-xs font-mono w-5">{i + 1}</span>
              <span className="font-mono font-semibold text-blue-light w-14">{s.ticker}</span>
              <span className="text-text-primary flex-1 truncate hidden sm:block">{s.name}</span>
              <span className="text-text-muted text-xs hidden md:block">{s.sector}</span>
              <span className="font-mono font-bold text-emerald text-right w-14 shrink-0">
                {fmtPct(s.current_yield)}
              </span>
              <ArrowUpRight size={13} className="text-text-muted shrink-0" />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Dividend History Chart ────────────────────────────────────────────────────

function HistoryChart({ ticker, onClose }: { ticker: string; onClose: () => void }) {
  const { history, historyLoading, fetchHistory } = useDividends()

  useEffect(() => {
    fetchHistory(ticker)
  }, [ticker, fetchHistory])

  const chartData = (history?.history ?? []).slice(-40).map(p => ({
    date: p.date.slice(0, 7),
    amount: p.amount,
  }))

  return (
    <div className="card border-blue/30 p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-text-primary flex items-center gap-2">
          <BarChart2 size={16} className="text-blue-light" />
          {ticker} Dividend History
          {history && (
            <span className="text-xs text-text-muted font-normal ml-1">
              TTM: {fmtMoney(history.ttm_dividend, 4)} · Yield: {fmtPct(history.current_yield)}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="btn-secondary text-xs px-2 py-1"
        >
          Close
        </button>
      </div>

      {historyLoading ? (
        <div className="h-48 bg-surface-card rounded animate-pulse" />
      ) : chartData.length === 0 ? (
        <p className="text-text-muted text-sm">No dividend history available for {ticker}.</p>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => `$${v.toFixed(2)}`}
              width={48}
            />
            <Tooltip
              formatter={(v: number) => [`$${v.toFixed(4)}`, 'Dividend']}
              labelFormatter={(l: string) => `Period: ${l}`}
              contentStyle={{
                background: 'var(--color-surface-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                fontSize: '12px', color: 'rgb(var(--text-primary))'}}
            />
            <Bar dataKey="amount" fill="#10b981" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function DividendsPage() {
  const { fetchPortfolio, fetchCalendar, fetchAristocrats, fetchTopYield } = useDividends()
  const [selectedTicker, setSelectedTicker] = useState<string | null>(null)

  useEffect(() => {
    fetchPortfolio()
    fetchCalendar(90)
    fetchAristocrats()
    fetchTopYield(20)
  }, [fetchPortfolio, fetchCalendar, fetchAristocrats, fetchTopYield])

  const handleSelectTicker = useCallback((ticker: string) => {
    setSelectedTicker(prev => prev === ticker ? null : ticker)
  }, [])

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-emerald/10 flex items-center justify-center">
          <Banknote size={18} className="text-emerald" />
        </div>
        <div>
          <h1 className="page-title">Dividend Income</h1>
          <p className="text-xs text-text-muted">Track dividend income and forecast future payments</p>
        </div>
      </div>

      <IncomeTabs />

      {/* Income summary KPI row */}
      <div className="grid grid-cols-3 gap-4">
        <IncomeSummary />
      </div>

      {/* Reliable vs speculative income split — only renders when confidence data present */}
      <div className="grid grid-cols-3 gap-4">
        <ConfidenceSplitBanner />
      </div>

      {/* Dividend history chart (shown when a ticker is selected) */}
      {selectedTicker && (
        <HistoryChart ticker={selectedTicker} onClose={() => setSelectedTicker(null)} />
      )}

      {/* Portfolio table + Monthly forecast */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <PortfolioTable onSelectTicker={handleSelectTicker} />
        <MonthlyForecast />
      </div>

      {/* Calendar + Top Yield */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <ExDateCalendar />
        <TopYieldPanel onSelectTicker={handleSelectTicker} />
      </div>

      {/* Aristocrats */}
      <AristocratsPanel onSelectTicker={handleSelectTicker} />
    </div>
  )
}
